/**
 * LES RÉGLAGES DE NOTIFICATION — le modèle de l'écran, sans le DOM.
 *
 * L'écran Profil montre, ligne par ligne, ce que la personne recevra. Quand
 * elle bascule un interrupteur « par défaut » (une catégorie), toutes les
 * lignes qui en dépendent doivent bouger SOUS SES YEUX, avant l'enregistrement.
 * La résolution est donc refaite ici, à l'identique de celle du serveur
 * (`reglageCanal`, packages/core/src/comms/policy.ts) ; un test compare les
 * deux sur tous les cas.
 *
 * Ordre : politique de l'organisation → réglage du type → réglage de la
 * catégorie (s'il a été CHOISI) → défaut du type.
 *
 * ═══ UN TYPE, PLUSIEURS CATÉGORIES (revue du 08/10/2026) ═══
 *
 * Une règle d'alerte part en Critique, Attention, Information ou
 * Rétablissement ; un écart à la finalisation en Attention ou en Information.
 * Le serveur décide avec la catégorie RÉELLE de chaque notification : résoudre
 * le type à sa seule catégorie par défaut affichait « E-mail coupé » pour
 * « Réseau du club injoignable » (Critique, e-mail compris), et un aller-retour
 * de l'interrupteur enregistrait un « coupé » qui faisait taire l'e-mail des
 * alertes critiques sans que rien à l'écran ait changé. Pour ces types :
 *   · chaque canal se résout catégorie par catégorie (`reglagesParCategorie`) ;
 *   · le geste est un CHOIX à trois issues — « Par catégorie » (aucun
 *     réglage de type), « Activé », « Coupé » — et sa note dit ce que chaque
 *     catégorie donnera ;
 *   · « Activé » ou « Coupé » choisi au menu s'écrit TOUJOURS, même égal à ce
 *     que chaque catégorie donne déjà : c'est ce choix qui tiendra si la
 *     catégorie est coupée ensuite (le récap du mardi gardé par e-mail quand
 *     « Attention → E-mail » ne l'est plus) ;
 *   · pour un INTERRUPTEUR (type à une seule catégorie), un retour à l'état
 *     enregistré, ou à ce que la catégorie donnerait de toute façon, ne laisse
 *     aucune trace dans le brouillon (`avecType`) ; de même dans la grille
 *     « par catégorie » (`avecCategorie`).
 */

export type Cat = 'CRITICAL' | 'WARNING' | 'INFO' | 'RECOVERY'
export type Ch = 'IN_APP' | 'EMAIL' | 'PUSH'
export type Mode = 'MANDATORY' | 'ALLOWED' | 'DISABLED'
export type Origine = 'ORGANISATION' | 'TYPE' | 'CATEGORIE' | 'DEFAUT'

export const CATS: Cat[] = ['CRITICAL', 'WARNING', 'INFO', 'RECOVERY']
export const CHS: Ch[] = ['IN_APP', 'EMAIL', 'PUSH']
export const CAT_LABEL: Record<Cat, string> = { CRITICAL: 'Critique', WARNING: 'Attention', INFO: 'Information', RECOVERY: 'Rétablissement' }
/** « Dans Gestion » plutôt que « In-App » : la conséquence, pas le moyen. */
export const CH_LABEL: Record<Ch, string> = { IN_APP: 'Dans Gestion', EMAIL: 'E-mail', PUSH: 'Push' }

export type CellPrefs = Partial<Record<Ch, boolean>>

/** Ce que la personne a CHOISI — une cellule absente laisse le défaut s'appliquer. */
export interface Brouillon {
  cats: Partial<Record<Cat, CellPrefs>>
  types: Record<string, CellPrefs>
  quiet: { enabled: boolean; start: string; end: string; allow_critical: boolean }
}

export interface TypeInfo {
  key: string
  label: string
  description: string
  default_category: Cat
  category_label: string
  /** Toutes les catégories sous lesquelles le type peut partir ; absent (serveur d'avant) = la seule par défaut. */
  categories?: Cat[]
  category_driven: boolean
  default_channels: Ch[] | 'CATEGORIE'
  allowed_channels: Ch[]
  receivable: boolean
  reason: string | null
  receive_permission_label: string | null
  channels: Array<{
    channel: Ch; available: boolean; on: boolean; origin: Origine; lock: 'MANDATORY' | 'DISABLED' | null
    /** Le détail par catégorie, pour un type qui en porte plusieurs ; `null` sinon. */
    by_category?: Array<{ category: Cat; category_label: string; on: boolean; origin: Origine; lock: 'MANDATORY' | 'DISABLED' | null }> | null
    /** Le réglage posé sur CE type ; `null` = aucun. */
    type_setting?: boolean | null
  }>
}

/** Les catégories d'un type, la par défaut en tête. */
export function categoriesDu(t: Pick<TypeInfo, 'default_category' | 'categories'>): Cat[] {
  const liste = t.categories && t.categories.length > 0 ? t.categories : [t.default_category]
  return [t.default_category, ...liste.filter((c) => c !== t.default_category)]
}
export interface ModuleInfo { module_id: string | null; module_label: string; types: TypeInfo[] }
export interface FamilleInfo { family_id: string; family_label: string; modules: ModuleInfo[] }

export interface PolitiqueLigne { category: Cat; channel: Ch; mode: Mode }

export function modeDe(policy: PolitiqueLigne[], c: Cat, ch: Ch): Mode {
  return policy.find((p) => p.category === c && p.channel === ch)?.mode ?? 'ALLOWED'
}

export interface Reglage { on: boolean; origine: Origine; verrou: 'MANDATORY' | 'DISABLED' | null }

/**
 * La valeur d'un canal pour un type, dans UNE catégorie (par défaut, la
 * sienne) — le miroir de `reglageCanal` côté serveur.
 */
export function reglageDuType(
  t: Pick<TypeInfo, 'key' | 'default_category' | 'default_channels' | 'allowed_channels'>,
  ch: Ch, b: Pick<Brouillon, 'cats' | 'types'>, defaults: Record<Cat, Record<Ch, boolean>>, policy: PolitiqueLigne[],
  categorie: Cat = t.default_category,
): Reglage {
  if (!t.allowed_channels.includes(ch)) return { on: false, origine: 'DEFAUT', verrou: null }
  const mode = modeDe(policy, categorie, ch)
  if (mode === 'DISABLED') return { on: false, origine: 'ORGANISATION', verrou: 'DISABLED' }
  if (mode === 'MANDATORY') return { on: true, origine: 'ORGANISATION', verrou: 'MANDATORY' }
  const duType = b.types[t.key]?.[ch]
  if (typeof duType === 'boolean') return { on: duType, origine: 'TYPE', verrou: null }
  const deLaCategorie = b.cats[categorie]?.[ch]
  if (typeof deLaCategorie === 'boolean') return { on: deLaCategorie, origine: 'CATEGORIE', verrou: null }
  const defaut = t.default_channels === 'CATEGORIE' ? defaults[categorie][ch] : t.default_channels.includes(ch)
  return { on: defaut, origine: 'DEFAUT', verrou: null }
}

/** Chaque catégorie que le type peut porter, et ce que le serveur décidera pour elle. */
export function reglagesParCategorie(
  t: Pick<TypeInfo, 'key' | 'default_category' | 'categories' | 'default_channels' | 'allowed_channels'>,
  ch: Ch, b: Pick<Brouillon, 'cats' | 'types'>, defaults: Record<Cat, Record<Ch, boolean>>, policy: PolitiqueLigne[],
): Array<{ categorie: Cat; reglage: Reglage }> {
  return categoriesDu(t).map((categorie) => ({ categorie, reglage: reglageDuType(t, ch, b, defaults, policy, categorie) }))
}

/** Le choix posé sur un type, pour un canal : aucun (il suit sa catégorie), activé, coupé. */
export type ChoixType = 'SELON' | 'ACTIVE' | 'COUPE'
export function choixDuType(b: Pick<Brouillon, 'types'>, key: string, ch: Ch): ChoixType {
  const v = b.types[key]?.[ch]
  return v === true ? 'ACTIVE' : v === false ? 'COUPE' : 'SELON'
}
export const CHOIX_LABEL: Record<ChoixType, string> = { SELON: 'Par catégorie', ACTIVE: 'Activé', COUPE: 'Coupé' }

const liste = (cs: Cat[]): string => cs.map((c) => CAT_LABEL[c]).join(', ')

/**
 * CE QUE CHAQUE CATÉGORIE DONNERA, en une phrase entière (jamais tronquée) :
 * « Envoyé pour : Critique », « Envoyé pour toutes les catégories », et les
 * catégories que l'organisation impose ou coupe.
 */
export function resumeParCategorie(parCat: Array<{ categorie: Cat; on: boolean; verrou: 'MANDATORY' | 'DISABLED' | null }>): string {
  const oui = parCat.filter((x) => x.on).map((x) => x.categorie)
  const envoi = oui.length === parCat.length ? 'Envoyé pour toutes les catégories'
    : oui.length === 0 ? 'Envoyé pour aucune catégorie'
      : `Envoyé pour : ${liste(oui)}`
  const imposes = parCat.filter((x) => x.verrou === 'MANDATORY').map((x) => x.categorie)
  const coupes = parCat.filter((x) => x.verrou === 'DISABLED').map((x) => x.categorie)
  return [
    envoi,
    imposes.length > 0 ? `imposé par l’organisation pour : ${liste(imposes)}` : null,
    coupes.length > 0 ? `coupé par l’organisation pour : ${liste(coupes)}` : null,
  ].filter((x): x is string => x !== null).join(' · ')
}

/** La valeur affichée d'une cellule « par défaut » (catégorie × canal). */
export function reglageDeCategorie(
  c: Cat, ch: Ch, b: Pick<Brouillon, 'cats'>, defaults: Record<Cat, Record<Ch, boolean>>, policy: PolitiqueLigne[],
): Reglage {
  const mode = modeDe(policy, c, ch)
  if (mode === 'DISABLED') return { on: false, origine: 'ORGANISATION', verrou: 'DISABLED' }
  if (mode === 'MANDATORY') return { on: true, origine: 'ORGANISATION', verrou: 'MANDATORY' }
  const v = b.cats[c]?.[ch]
  if (typeof v === 'boolean') return { on: v, origine: 'CATEGORIE', verrou: null }
  return { on: defaults[c][ch], origine: 'DEFAUT', verrou: null }
}

/** La petite phrase sous un interrupteur : ce qui décide, quand ce n'est pas la personne. */
export function noteDuReglage(r: Reglage, categorie: Cat): string | null {
  if (r.verrou === 'MANDATORY') return 'Imposé par l’organisation'
  if (r.verrou === 'DISABLED') return 'Coupé par l’organisation'
  if (r.origine === 'CATEGORIE') return `Suit votre choix « ${CAT_LABEL[categorie]} »`
  return null
}

/** Le brouillon tiré de ce que le serveur a enregistré (cellules choisies seulement). */
export function brouillonDepuis(
  prefs: (Partial<Record<Cat, CellPrefs>> & { types?: Record<string, CellPrefs> }) | null,
  quiet: Brouillon['quiet'] | null,
): Brouillon {
  const cats: Brouillon['cats'] = {}
  for (const c of CATS) {
    const row = prefs?.[c]
    if (!row) continue
    const cellules: CellPrefs = {}
    for (const ch of CHS) if (typeof row[ch] === 'boolean') cellules[ch] = row[ch]
    if (Object.keys(cellules).length > 0) cats[c] = cellules
  }
  const types: Brouillon['types'] = {}
  for (const [k, row] of Object.entries(prefs?.types ?? {})) {
    const cellules: CellPrefs = {}
    for (const ch of CHS) if (typeof row[ch] === 'boolean') cellules[ch] = row[ch]
    if (Object.keys(cellules).length > 0) types[k] = cellules
  }
  return { cats, types, quiet: quiet ?? { enabled: false, start: '23:00', end: '08:00', allow_critical: true } }
}

/** Ce que le PUT reçoit : les catégories choisies, et les types (toujours envoyés depuis le Web). */
export function corpsDuPut(b: Brouillon): { prefs: Record<string, unknown>; quiet_hours: Brouillon['quiet'] } {
  return { prefs: { ...b.cats, types: b.types }, quiet_hours: b.quiet }
}

/**
 * Basculer une cellule de type : le brouillon suivant, sans muter le précédent.
 *
 * `v = null` retire le réglage (le type suit sa catégorie). Avec le contexte
 * de l'écran, pour un INTERRUPTEUR (type à une seule catégorie) : un geste qui
 * ramène à l'état ENREGISTRÉ le reprend tel quel, et un « oui »/« non » qui
 * égale ce que la catégorie donnerait sans réglage de type ne laisse pas de
 * cellule — un aller-retour ne rend jamais la page « modifiée », et
 * n'enregistre jamais un réglage invisible. Le MENU à trois choix (plusieurs
 * catégories) n'est jamais réduit : « Activé » égal à l'état courant était
 * refusé en silence, et ne protégeait donc pas le type d'un réglage de
 * catégorie posé ensuite (revue du 08/10/2026) ; son aller-retour passe par
 * « Par catégorie » (`v = null`).
 */
export function avecType(
  b: Brouillon, key: string, ch: Ch, v: boolean | null,
  contexte?: {
    t: Pick<TypeInfo, 'key' | 'default_category' | 'categories' | 'default_channels' | 'allowed_channels'>
    defaults: Record<Cat, Record<Ch, boolean>>
    policy: PolitiqueLigne[]
    reference: Pick<Brouillon, 'types'> | null
  },
): Brouillon {
  const sans = (): Brouillon => {
    const { [ch]: _retire, ...reste } = b.types[key] ?? {}
    const types = { ...b.types }
    if (Object.keys(reste).length > 0) types[key] = reste
    else delete types[key]
    return { ...b, types }
  }
  if (v === null) return sans()
  if (contexte && categoriesDu(contexte.t).length === 1) {
    const enregistre = contexte.reference?.types[key]?.[ch]
    if (enregistre === undefined) {
      const sansReglage = sans()
      const memeChose = reglagesParCategorie(contexte.t, ch, sansReglage, contexte.defaults, contexte.policy)
        .every((x) => x.reglage.verrou !== null || x.reglage.on === v)
      if (memeChose) return sansReglage
    }
  }
  return { ...b, types: { ...b.types, [key]: { ...b.types[key], [ch]: v } } }
}
/**
 * Basculer une cellule « par défaut » (catégorie × canal) : le brouillon
 * suivant, sans muter le précédent.
 *
 * Un « coupé » CHOISI n'est pas le défaut : il passe devant le défaut du type
 * (`reglageCanal` : politique → type → catégorie choisie → défaut du type).
 * Activer puis recouper « Attention → E-mail » enregistrait donc
 * `WARNING.EMAIL = false`, qui faisait taire l'e-mail du récapitulatif du
 * mardi et des alertes Comptabilité (e-mail par défaut) sans que rien ait
 * changé à l'écran (revue du 08/10/2026). Avec le contexte de l'écran : un
 * geste qui ramène à la valeur ENREGISTRÉE la reprend telle quelle ; un geste
 * qui ramène au défaut d'une cellule jamais enregistrée la retire.
 */
export function avecCategorie(
  b: Brouillon, c: Cat, ch: Ch, v: boolean,
  contexte?: { defaults: Record<Cat, Record<Ch, boolean>>; reference: Pick<Brouillon, 'cats'> | null },
): Brouillon {
  if (contexte) {
    const enregistre = contexte.reference?.cats[c]?.[ch]
    if (enregistre === undefined && v === contexte.defaults[c][ch]) {
      const { [ch]: _retire, ...reste } = b.cats[c] ?? {}
      const cats = { ...b.cats }
      if (Object.keys(reste).length > 0) cats[c] = reste
      else delete cats[c]
      return { ...b, cats }
    }
  }
  return { ...b, cats: { ...b.cats, [c]: { ...b.cats[c], [ch]: v } } }
}

/**
 * Deux brouillons disent-ils la même chose ? Indépendant de l'ordre des clés :
 * une cellule retirée puis remise revient en fin d'objet, et une comparaison
 * de JSON brut aurait laissé la barre d'enregistrement allumée sur une page
 * identique.
 */
export function memeBrouillon(a: Brouillon | null, b: Brouillon | null): boolean {
  if (a === null || b === null) return a === b
  const canon = (x: Brouillon): string => {
    const cellules = (r: CellPrefs | undefined): string => CHS.map((ch) => `${ch}=${r?.[ch] === undefined ? '-' : String(r[ch])}`).join(',')
    const cats = CATS.map((c) => `${c}:${cellules(x.cats[c])}`).join(';')
    const types = Object.keys(x.types).filter((k) => Object.keys(x.types[k] ?? {}).length > 0).sort()
      .map((k) => `${k}:${cellules(x.types[k])}`).join(';')
    return `${cats}|${types}|${JSON.stringify([x.quiet.enabled, x.quiet.start, x.quiet.end, x.quiet.allow_critical])}`
  }
  return canon(a) === canon(b)
}

/** Des réglages choisis existent-ils (le bouton « revenir aux réglages proposés ») ? */
export function aDesChoix(b: Pick<Brouillon, 'cats' | 'types'>): boolean {
  return Object.values(b.cats).some((r) => r && Object.keys(r).length > 0)
    || Object.values(b.types).some((r) => Object.keys(r).length > 0)
}
